import axios from 'axios';
import ExcelJS from 'exceljs';
import Cookies from 'js-cookie';
import regenerateToken from '../components/regenerateToken';

// ─── API Configuration ────────────────────────────────────────────────────────
const MAIL_API_URL = 'https://917sfoy1xc.execute-api.ap-south-1.amazonaws.com/Testing/notifications/email';
const API_BASE = 'https://ap94204xve.execute-api.ap-south-1.amazonaws.com';
const PARAMETER_MAPPING_API = `${API_BASE}/prod/mapping/parameter`;
const PROFILE_MAPPING_API = `${API_BASE}/prod/mapping/profile`;

// Stage priority for presign endpoint
const PRESIGN_STAGES = ['prod', 'dev'];

// Default CC
const DEFAULT_CC = ['sairam@niroggyan.com', 'nithish@niroggyan.com', 'operations@niroggyan.com', 'tech@niroggyan.com'];

// NirogGyan brand colours (used in Excel + email)
const BRAND = {
  blue: '3567D6',  // Header background
  blueLight: 'EFF6FF',  // Profile group row background
  blueMid: 'DBEAFE',  // Sheet 2 summary header
  white: 'FFFFFF',
  rowAlt: 'F8FAFC',  // Alternating data row
  textDark: '0F172A',
  textMid: '334155',
  textBlue: '1E40AF',
  border: 'E2E8F0',
};

// ─── Token Helper ─────────────────────────────────────────────────────────────
async function getValidToken() {
  return Cookies.get('idToken');
}

// ─── Data Fetchers ────────────────────────────────────────────────────────────

/**
 * Fetch parameter mapping data.
 * Returns: { parameterId: ngStandardName, ... }
 */
export async function fetchParameterMappingData() {
  let idToken = await getValidToken();
  // Only accept 2xx and 401 (to handle token refresh); everything else throws.
  const validateStatus = (s) => (s >= 200 && s < 300) || s === 401;
  const config = {
    headers: { authorization: idToken },
    validateStatus,
  };

  let res = await axios.get(PARAMETER_MAPPING_API, config);
  if (res.status === 401) {
    await regenerateToken();
    idToken = Cookies.get('idToken');
    res = await axios.get(PARAMETER_MAPPING_API, { ...config, headers: { authorization: idToken } });
    if (res.status === 401) throw new Error('Unauthorized: Token refresh failed for parameter mapping.');
  }
  if (!res?.data?.isSuccess) throw new Error('Failed to fetch parameter mapping data.');
  return res.data?.data?.idMapping || {};
}

/**
 * Fetch profile mapping data.
 * Returns: { testName (NGStandardName): profileName, ... }
 */
export async function fetchProfileMappingData() {
  let idToken = await getValidToken();
  // Only accept 2xx and 401 (to handle token refresh); everything else throws.
  const validateStatus = (s) => (s >= 200 && s < 300) || s === 401;
  const config = {
    headers: { authorization: idToken },
    validateStatus,
  };

  let res = await axios.get(PROFILE_MAPPING_API, config);
  if (res.status === 401) {
    await regenerateToken();
    idToken = Cookies.get('idToken');
    res = await axios.get(PROFILE_MAPPING_API, { ...config, headers: { authorization: idToken } });
    if (res.status === 401) throw new Error('Unauthorized: Token refresh failed for profile mapping.');
  }
  if (!res?.data?.isSuccess) throw new Error('Failed to fetch profile mapping data.');
  return res.data?.data?.profileMapping || {};
}

// ─── Pre-signed URL Helpers ───────────────────────────────────────────────────

/**
 * Fetch a pre-signed S3 URL (putObject or getObject).
 * Handles all known API response shapes.
 */
export async function fetchPresignedUrl(org, center, operation) {
  const params = new URLSearchParams({ org, center, operation });
  let lastErr = null;

  for (const stage of PRESIGN_STAGES) {
    const endpointUrl = `${API_BASE}/${stage}/mapping/url/presign?${params}`;
    let idToken = await getValidToken();

    // Only accept 2xx, 401 (token refresh), 404 (try next stage); everything else throws.
    const presignValidateStatus = (s) => (s >= 200 && s < 300) || s === 401 || s === 404;

    let response = await axios.get(endpointUrl, {
      headers: { Authorization: idToken },
      validateStatus: presignValidateStatus,
    });

    if (response.status === 401) {
      await regenerateToken();
      idToken = Cookies.get('idToken');
      response = await axios.get(endpointUrl, {
        headers: { Authorization: idToken },
        validateStatus: presignValidateStatus,
      });
    }
    if (response.status === 401) throw new Error('Unauthorized: Token refresh failed.');
    if (response.status === 404) continue;

    // Shape A: { isSuccess: true, data: "https://..." }  ← string directly
    // Shape B: { data: { url: "https://..." } }          ← nested object
    // Shape C: { presignedUrl: "https://..." }           ← flat object
    const rawData = response?.data?.data ?? response?.data;

    if (typeof rawData === 'string' && rawData.startsWith('http')) return rawData;

    const presignedUrl =
      rawData?.url ?? rawData?.presignedUrl ?? rawData?.presigned_url ?? null;
    if (presignedUrl) return presignedUrl;

    console.warn(`[Presign][${stage}] Unrecognised response:`, response?.data);
    lastErr = new Error('Presign API returned an unrecognised response format.');
  }

  throw lastErr || new Error('Presign API failed on all stages.');
}

/**
 * Upload binary buffer to S3 via pre-signed PUT URL.
 */
export async function uploadFileToPresignedUrl(uploadUrl, fileBuffer, contentType) {
  await axios.put(uploadUrl, fileBuffer, {
    headers: { 'Content-Type': contentType },
    validateStatus: (s) => s >= 200 && s < 300,
  });
}

// ─── Excel Builder ────────────────────────────────────────────────────────────

/**
 * Build a styled Excel workbook (2 sheets) from both mapping datasets.
 *
 * Sheet 1 — "Parameter Mapping"
 *   Columns: Profile Name | Parameter Name | Parameter ID
 *   Rows sorted by Profile Name → Parameter Name
 *   Profile group rows visually highlighted
 *
 * Sheet 2 — "Profile Summary"
 *   Columns: Profile Name | Total Parameters | Parameter Names (comma list)
 *
 * @param {Object} idMapping      - { parameterId: ngStandardName }
 * @param {Object} profileMapping - { ngStandardName: profileName }
 * @returns {Promise<Buffer>}     - Excel file buffer
 */
export async function buildCombinedMappingXLSX(idMapping, profileMapping) {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'NirogGyan Config Portal';
  wb.created = new Date();
  wb.modified = new Date();

  // ── Helper: apply header row style ──────────────────────────────────────
  const applyHeaderStyle = (row, bgHex = BRAND.blue, fgHex = BRAND.white) => {
    row.height = 22;
    row.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF' + bgHex } };
      cell.font = { name: 'Calibri', bold: true, size: 11, color: { argb: 'FF' + fgHex } };
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: false };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FF' + BRAND.border } },
        bottom: { style: 'thin', color: { argb: 'FF' + BRAND.border } },
        left: { style: 'thin', color: { argb: 'FF' + BRAND.border } },
        right: { style: 'thin', color: { argb: 'FF' + BRAND.border } },
      };
    });
  };

  // ── Helper: apply data cell style ───────────────────────────────────────
  const applyDataStyle = (cell, bgHex, bold = false, colorHex = BRAND.textDark, align = 'left') => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF' + bgHex } };
    cell.font = { name: 'Calibri', size: 10, bold, color: { argb: 'FF' + colorHex } };
    cell.alignment = { vertical: 'middle', horizontal: align, wrapText: false };
    cell.border = {
      top: { style: 'hair', color: { argb: 'FFE2E8F0' } },
      bottom: { style: 'hair', color: { argb: 'FFE2E8F0' } },
      left: { style: 'hair', color: { argb: 'FFE2E8F0' } },
      right: { style: 'hair', color: { argb: 'FFE2E8F0' } },
    };
  };

  // ════════════════════════════════════════════════════════════════════════
  // SHEET 1 — Parameter Mapping
  // Columns: Profile Name | Parameter Name | Parameter ID
  // ════════════════════════════════════════════════════════════════════════
  const ws1 = wb.addWorksheet('Parameter Mapping', {
    views: [{ state: 'frozen', ySplit: 3 }], // freeze top 3 rows (title + subtitle + header)
  });

  ws1.columns = [
    { key: 'profile', width: 28 },
    { key: 'paramName', width: 38 },
    { key: 'paramId', width: 24 },
  ];

  // ── Title row ──────────────────────────────────────────────────────────
  ws1.mergeCells('A1:C1');
  const titleCell = ws1.getCell('A1');
  titleCell.value = 'NirogGyan — Parameter & Profile Mapping Report';
  titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF' + BRAND.blue } };
  titleCell.font = { name: 'Calibri', bold: true, size: 14, color: { argb: 'FFFFFFFF' } };
  titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
  ws1.getRow(1).height = 28;

  // ── Subtitle row ───────────────────────────────────────────────────────
  ws1.mergeCells('A2:C2');
  const subCell = ws1.getCell('A2');
  subCell.value = `Generated: ${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}   |   Total Parameters: ${Object.keys(idMapping).length}`;
  subCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF' + BRAND.blueLight } };
  subCell.font = { name: 'Calibri', italic: true, size: 10, color: { argb: 'FF' + BRAND.textBlue } };
  subCell.alignment = { vertical: 'middle', horizontal: 'center' };
  ws1.getRow(2).height = 18;

  // ── Column header row ──────────────────────────────────────────────────
  const headerRow1 = ws1.addRow(['Profile Name', 'Parameter Name', 'Parameter ID']);
  applyHeaderStyle(headerRow1);

  // ── Build combined rows ────────────────────────────────────────────────
  // Join: idMapping { id -> ngName } + profileMapping { ngName -> profile }
  const combinedRows = Object.entries(idMapping).map(([paramId, ngName]) => ({
    profile: profileMapping[ngName] || '(Not Assigned)',
    paramName: ngName,
    paramId,
  }));

  // Sort: Profile Name (Not Assigned last) → Parameter Name
  combinedRows.sort((a, b) => {
    if (a.profile === '(Not Assigned)' && b.profile !== '(Not Assigned)') return 1;
    if (b.profile === '(Not Assigned)' && a.profile !== '(Not Assigned)') return -1;
    if (a.profile !== b.profile) return a.profile.localeCompare(b.profile);
    return a.paramName.localeCompare(b.paramName);
  });

  // ── Write data rows with profile group separators ─────────────────────
  let lastProfile = null;
  let rowIndex = 0;

  for (const row of combinedRows) {
    // Insert a profile group separator row when profile changes.
    // IMPORTANT: addRow first so rowCount is correct, THEN mergeCells.
    if (row.profile !== lastProfile) {
      const groupRow = ws1.addRow([row.profile, '', '']);
      groupRow.height = 18;
      // Merge across the newly created row using its actual row number
      ws1.mergeCells(`A${groupRow.number}:C${groupRow.number}`);
      const gc = groupRow.getCell(1);
      gc.value = `  ${row.profile}`;
      gc.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF' + BRAND.blueLight } };
      gc.font = { name: 'Calibri', bold: true, italic: true, size: 10, color: { argb: 'FF' + BRAND.textBlue } };
      gc.alignment = { vertical: 'middle', horizontal: 'left' };
      gc.border = {
        top: { style: 'medium', color: { argb: 'FF' + BRAND.blue } },
        bottom: { style: 'thin', color: { argb: 'FF' + BRAND.border } },
        left: { style: 'medium', color: { argb: 'FF' + BRAND.blue } },
        right: { style: 'thin', color: { argb: 'FF' + BRAND.border } },
      };
      lastProfile = row.profile;
      rowIndex = 0;
    }

    const bg = rowIndex % 2 === 0 ? BRAND.white : BRAND.rowAlt;
    const dataRow = ws1.addRow([row.profile, row.paramName, row.paramId]);
    dataRow.height = 16;

    applyDataStyle(dataRow.getCell(1), bg, false, BRAND.textMid, 'left');
    applyDataStyle(dataRow.getCell(2), bg, false, BRAND.textDark, 'left');
    applyDataStyle(dataRow.getCell(3), bg, false, BRAND.textMid, 'center');

    rowIndex++;
  }

  // ── Write to buffer ────────────────────────────────────────────────────
  // writeBuffer() returns an ArrayBuffer in browser environments.
  // Do NOT use Buffer.from() — Buffer is Node.js only and throws in browsers.
  return await wb.xlsx.writeBuffer();
}

// ─── Full Upload Orchestration ────────────────────────────────────────────────

/**
 * Orchestrates:
 *   1. Fetch PUT pre-signed URL
 *   2. Upload combined XLSX to S3
 *   3. Fetch GET pre-signed URL (download link)
 *
 * @param {string} org            - userData.org
 * @param {string} center         - userData.center
 * @param {Object} idMapping      - { parameterId: ngStandardName }
 * @param {Object} profileMapping - { ngStandardName: profileName }
 * @returns {Promise<string>}     - S3 download URL
 */
export async function uploadMappingAndGetDownloadUrl(org, center, idMapping, profileMapping) {
  // Step A: Get PUT pre-signed URL
  const putUrl = await fetchPresignedUrl(org, center, 'putObject');

  // Step B: Build styled combined XLSX
  const xlsxBuffer = await buildCombinedMappingXLSX(idMapping, profileMapping);

  // Step C: Upload to S3
  await uploadFileToPresignedUrl(
    putUrl,
    xlsxBuffer,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  );

  // Step D: Get download pre-signed URL
  const downloadUrl = await fetchPresignedUrl(org, center, 'getObject');
  return downloadUrl;
}

// ─── Email HTML Template ──────────────────────────────────────────────────────

/**
 * Generates a clean, branded HTML email with a download button.
 *
 * @param {number} paramCount     - Total parameters mapped
 * @param {number} profileCount   - Total unique profiles
 * @param {string} clientName     - Display name of the client
 * @param {string} dateSent       - Date string
 * @param {string} downloadUrl    - S3 pre-signed download URL
 * @returns {string}              - Full HTML email string
 */
export function getMappingReportEmailHtml(paramCount, profileCount, clientName, dateSent, downloadUrl) {
  const esc = (s) =>
    String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  const NIROGGYAN_LOGO_URL =
    'https://niroggyan.s3.ap-south-1.amazonaws.com/new-billing-dashboard-files/logos/niroggyan-logo.svg';

  const client = esc(clientName) || 'Client';
  const pCount = esc(String(paramCount ?? 0));
  const prCount = esc(String(profileCount ?? 0));
  const date = esc(dateSent ?? new Date().toLocaleDateString());
  const safeUrl = esc(downloadUrl);

  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Parameter & Profile Mapping Report - NirogGyan</title>
</head>
<body style="margin:0; padding:0; font-family:'Segoe UI',Arial,Helvetica,sans-serif; background-color:#f1f5f9;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0"
         style="background-color:#f1f5f9; padding:40px 16px;">
    <tr>
      <td align="center">
        <table role="presentation" width="100%" cellspacing="0" cellpadding="0"
               style="max-width:600px; background-color:#ffffff; border-radius:16px;
                      box-shadow:0 4px 24px rgba(15,23,42,0.10); overflow:hidden;">

          <!-- Header -->
          <tr>
            <td style="background:linear-gradient(135deg,#3567d6 0%,#2c5aa0 100%);
                        padding:32px 40px; text-align:center;">

              <!-- Text logo — works in all email clients, no image dependency -->
              <div style="display:inline-block; margin-bottom:14px;">
                <span style="font-family:'Segoe UI',Arial,sans-serif; font-size:26px;
                             font-weight:800; color:#ffffff; letter-spacing:-0.03em;">
                  Nirog<span style="color:#93c5fd;">Gyan</span>
                </span>
              </div>

              <h1 style="margin:0; color:#ffffff; font-size:20px;
                          font-weight:700; letter-spacing:-0.01em; line-height:1.4;">
                Parameter &amp; Profile Mapping Report
              </h1>
              <p style="margin:8px 0 0 0; color:rgba(255,255,255,0.75); font-size:13px;
                         letter-spacing:0.02em; text-transform:uppercase;">
                Config Portal &nbsp;·&nbsp; Onboarding Status
              </p>
            </td>
          </tr>

          <!-- Body -->
          <tr>
            <td style="padding:36px 40px;">

              <p style="margin:0 0 6px 0; color:#0f172a; font-size:16px; font-weight:600;">
                Hello ${client},
              </p>
              <p style="margin:0 0 28px 0; color:#64748b; font-size:15px; line-height:1.65;">
                Your parameter and profile mapping has been completed. Please find the full
                mapping report attached below — it includes both the parameter-level mapping
                and the profile-level grouping in a single Excel file.
              </p>

              <!-- Summary stats -->
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0"
                     style="border:1px solid #e2e8f0; border-radius:10px;
                            border-collapse:separate; overflow:hidden;">
                <tr style="background:#f8fafc;">
                  <td style="padding:14px 20px; font-size:13px; color:#64748b;
                              width:50%; border-bottom:1px solid #e2e8f0;">
                    <strong style="color:#334155;">Parameters Mapped</strong>
                  </td>
                  <td style="padding:14px 20px; font-size:18px; color:#3567d6;
                              font-weight:700; border-bottom:1px solid #e2e8f0;">
                    ${pCount}
                  </td>
                </tr>
                <tr>
                  <td style="padding:14px 20px; font-size:13px; color:#64748b;">
                    <strong style="color:#334155;">Profiles Covered</strong>
                  </td>
                  <td style="padding:14px 20px; font-size:18px; color:#3567d6; font-weight:700;">
                    ${prCount}
                  </td>
                </tr>
                <tr>
                  <td style="padding:14px 20px; font-size:13px; color:#64748b;
                              border-top:1px solid #e2e8f0;">
                    <strong style="color:#334155;">Report Date</strong>
                  </td>
                  <td style="padding:14px 20px; font-size:14px; color:#0f172a;
                              border-top:1px solid #e2e8f0;">
                    ${date}
                  </td>
                </tr>
              </table>

              <!-- Download section -->
              <div style="margin:28px 0 0 0; padding:24px; background:#eff6ff;
                          border-radius:12px; border:1px solid #bfdbfe; text-align:center;">
                 <p style="margin:0 0 6px 0; font-size:14px; color:#1e40af; font-weight:600;">
                   Download Your Mapping Report
                 </p>
                 <p style="margin:0 0 20px 0; font-size:13px; color:#64748b; line-height:1.6;">
                   The Excel file contains the full mapping with columns:<br/>
                   <strong style="color:#334155;">Profile Name &nbsp;|&nbsp; Parameter Name &nbsp;|&nbsp; Parameter ID</strong><br/>
                   Rows are sorted by profile, then by parameter name.
                 </p>
                <a href="${safeUrl}"
                   style="display:inline-block; padding:14px 32px; background:#3567d6;
                          color:#ffffff !important; text-decoration:none; font-weight:700;
                          font-size:15px; border-radius:8px; letter-spacing:0.01em;">
                  ⬇ Download Excel Report
                </a>
                <p style="margin:16px 0 0 0; font-size:11px; color:#94a3b8;">
                  This link is time-limited. Please download within 24 hours.
                </p>
              </div>

              <p style="margin:32px 0 0 0; font-size:12px; color:#94a3b8;
                         border-top:1px solid #f1f5f9; padding-top:20px;">
                This report was automatically generated and sent from the NirogGyan Config Portal —
                Onboarding Status module. If you have questions, contact your NirogGyan onboarding manager.
              </p>

            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>
  `.trim();
}

// ─── Legacy Helpers (kept for backward compatibility) ─────────────────────────

/** Build a simple CSV string from idMapping only (legacy / fallback use) */
export function buildMappingCSV(idMapping) {
  if (!idMapping || typeof idMapping !== 'object') return '';
  const esc = (f) => {
    const s = String(f ?? '');
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const hdrs = ['Parameter ID', 'NG Standard Name'];
  const rows = Object.entries(idMapping).map(([id, name]) => [esc(id), esc(name)].join(','));
  return '\uFEFF' + [hdrs.map(esc).join(','), ...rows].join('\n');
}

// ─── Send Email ───────────────────────────────────────────────────────────────

/**
 * Sends the mapping report email with a download link.
 *
 * @param {Object}   options
 * @param {string[]} options.toEmail   - Recipient email(s)
 * @param {string[]} [options.cc]      - CC recipients
 * @param {string}   options.emailBody - HTML email body
 * @param {string}   options.subject   - Email subject
 * @returns {Promise<{success: boolean, data?: any, error?: any}>}
 */
export async function sendMappingReportEmail({ toEmail, cc = DEFAULT_CC, emailBody, subject }) {
  if (!toEmail || !Array.isArray(toEmail) || toEmail.length === 0) {
    return { success: false, error: new Error('Client email (toEmail) is required.') };
  }

  const maildata = {
    fromEmail: ['reports@niroggyan.com'],
    toEmail,
    // Use undefined-check so callers can pass null/[] and have it respected;
    // only fall back to DEFAULT_CC when cc is strictly undefined.
    cc: cc !== undefined ? cc : DEFAULT_CC,
    attachments: [],
    withAttachments: 'false',
    bodyType: 'html',
    subject,
    emailBody,
  };

  try {
    const response = await axios.post(MAIL_API_URL, maildata, {
      headers: { 'Content-Type': 'application/json' },
    });
    return { success: true, data: response?.data };
  } catch (error) {
    return { success: false, error };
  }
}
